import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);
  const category = useMemo(
    () => categories.find((c) => c.slug === article?.categorySlug),
    [article]
  );

  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
            .slice(0, 3)
        : [],
    [article]
  );

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到 - 麻豆影视" description="您访问的文章不存在或已被移除。" keywords="麻豆影视,影视资讯" canonicalUrl="https://www.madouys.com/" />
        <ScrollToTop />
        <Navbar />
        <main className="pt-24 pb-20">
          <div className="max-w-[800px] mx-auto px-4 md:px-6 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章未找到</h1>
            <p className="text-foreground-600 mb-8">您访问的文章不存在或已被移除。</p>
            <Link to="/" className="inline-flex px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${article.title} - 麻豆影视在线观看`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.madouys.com/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
      />
      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.excerpt,
        image: article.coverImage,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.publishDate,
        publisher: { '@type': 'Organization', name: '麻豆影视', url: 'https://www.madouys.com/' },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.madouys.com/article/${article.id}` },
      }} />
      <SchemaOrg type="BreadcrumbList" data={{ itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.madouys.com/' },
        { '@type': 'ListItem', position: 2, name: category?.name || '分类', item: `https://www.madouys.com/category/${article.categorySlug}` },
        { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.madouys.com/article/${article.id}` },
      ] }} />

      <ScrollToTop />
      <Navbar />

      <main className="pt-24 pb-10 md:pb-14">
        <article className="max-w-[860px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: category?.name || '分类', href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          {/* Article Header */}
          <header className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-snug">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views.toLocaleString()} 次阅读
              </span>
            </div>
          </header>

          {/* Cover Image */}
          <div className="rounded-xl overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full aspect-[16/9] object-cover object-top"
              title={article.title}
            />
          </div>

          {/* Article Content */}
          <div
            className="prose prose-lg max-w-none mb-10 article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="flex flex-wrap items-center gap-2 mb-10 pt-6 border-t border-background-200/70">
            <span className="text-sm text-foreground-500">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="border-t border-background-200/70 pt-8">
              <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-5">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        title={ra.title}
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-1">{ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}